import { useField, useFormikContext } from "formik";
import EntityRecordSelect from "./EntityRecordSelect";
import { IEntity } from "@/types/breeze/Entity";
import { useEffect, useState } from "react";
import EntitySelect from "./EntitySelect";
import { Box, Text, Flex } from "@radix-ui/themes";
import { entityService } from "@/services/breeze/EntityService";

interface DynamicReferenceFieldProps {
  entityFieldName: string;
  recordFieldName: string;
  label: string;
  description?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
}

export default function DynamicReferenceField({
  entityFieldName,
  recordFieldName,
  label,
  description,
  required = false,
  className,
  disabled = false,
}: DynamicReferenceFieldProps) {
  const [selectedEntity, setSelectedEntity] = useState<IEntity | null>(null);
  const [entityField, entityMeta, entityHelpers] = useField(entityFieldName);
  const [recordField, recordMeta, recordHelpers] = useField(recordFieldName);
  const { setFieldValue } = useFormikContext();

  // Load the selected entity if entityField has a value (for edit scenarios)
  useEffect(() => {
    const loadSelectedEntity = async () => {
      if (entityField.value && !selectedEntity) {
        try {
          const entity = await entityService.getById(entityField.value);
          setSelectedEntity(entity);
        } catch (error) {
          console.error("Failed to load selected entity:", error);
        }
      }
    };

    loadSelectedEntity();
  }, [entityField.value, selectedEntity]);

  // Handle entity selection change
  const handleEntityChange = (entity: IEntity) => {
    setSelectedEntity(entity);
    setFieldValue(entityFieldName, entity.id);
    // Clear the record field when entity changes
    setFieldValue(recordFieldName, "");
  };

  // Handle record selection change
  const handleRecordChange = (newRecordId: any) => {
    setFieldValue(recordFieldName, newRecordId);
  };

  // Custom validation to ensure both entity and record are selected if required
  useEffect(() => {
    if (required) {
      if (entityField.value && !recordField.value) {
        recordHelpers.setError("Please select a record");
      } else if (!entityField.value && recordField.value) {
        entityHelpers.setError("Please select an entity");
      } else if (!entityField.value && !recordField.value) {
        entityHelpers.setError("Please select an entity");
        recordHelpers.setError("Please select a record");
      } else {
        // Clear errors when both are selected
        if (entityMeta.error) entityHelpers.setError(undefined);
        if (recordMeta.error) recordHelpers.setError(undefined);
      }
    }
  }, [
    entityField.value,
    recordField.value,
    required,
    entityHelpers,
    recordHelpers,
    entityMeta.error,
    recordMeta.error,
  ]);

  return (
    <Box mb="4" className={className}>
      <Box mb="2">
        {label && (
          <Text as="label" size="2" weight="medium" mb="1">
            {label} {required && <Text color="red">{" *"}</Text>}
          </Text>
        )}

        {description && (
          <Text size="2" color="gray" mb="2" style={{ display: "block" }}>
            {description}
          </Text>
        )}
      </Box>

      <Flex direction="row" gap="2" align="end">
        <Box flexGrow="1">
          <EntitySelect
            name={entityFieldName}
            valuePropName="id"
            label="Entity"
            required={required}
            disabled={disabled}
            options={{
              placeholder: "Select an entity",
            }}
            onEntitySelected={handleEntityChange}
          />
        </Box>

        <Box flexGrow="1">
          {selectedEntity ? (
            <EntityRecordSelect
              name={recordFieldName}
              label="Record"
              required={required}
              entity={selectedEntity}
              disabled={disabled}
              onChange={handleRecordChange}
            />
          ) : (
            <Box mb="4">
              <Text size="2" style={{ display: "block" }} mb="1">
                Record
              </Text>
              <Text size="2" color="gray">
                Select an entity first
              </Text>
            </Box>
          )}
        </Box>
      </Flex>

      {/* Show validation errors for either field */}
      {((entityMeta.error && entityMeta.touched) ||
        (recordMeta.error && recordMeta.touched)) && (
        <Text size="2" color="red" mt="1">
          {entityMeta.error || recordMeta.error}
        </Text>
      )}
    </Box>
  );
}
